/**
 * オフラインストレージ管理
 * Capacitor Preferencesを使用してローカルにデータをキャッシュ
 */

import { Preferences } from '@capacitor/preferences'
import type { Project, Todo } from './supabase'

const CACHE_KEYS = {
  PROJECTS: 'offline_cache_projects',
  TODOS: 'offline_cache_todos',
  PENDING_CHANGES: 'offline_pending_changes',
  LAST_SYNC: 'offline_last_sync',
  STATIC_CONTENT: 'offline_cache_static_content'
}

// 静的コンテンツのキャッシュ有効期限（24時間）
const STATIC_CONTENT_CACHE_TTL = 24 * 60 * 60 * 1000

export interface PendingChange {
  id: string
  type: 'create' | 'update' | 'delete'
  entity: 'project' | 'todo'
  data: any
  timestamp: number
}

/**
 * プロジェクトをキャッシュに保存
 */
export const cacheProjects = async (projects: Project[]): Promise<void> => {
  try {
    await Preferences.set({
      key: CACHE_KEYS.PROJECTS,
      value: JSON.stringify(projects)
    })
    console.log('Projects cached successfully')
  } catch (error) {
    console.error('Error caching projects:', error)
  }
}

/**
 * キャッシュからプロジェクトを取得
 */
export const getCachedProjects = async (): Promise<Project[] | null> => {
  try {
    const { value } = await Preferences.get({ key: CACHE_KEYS.PROJECTS })
    if (value) {
      return JSON.parse(value)
    }
    return null
  } catch (error) {
    console.error('Error getting cached projects:', error)
    return null
  }
}

/**
 * ToDoをキャッシュに保存
 */
export const cacheTodos = async (todos: Todo[]): Promise<void> => {
  try {
    await Preferences.set({
      key: CACHE_KEYS.TODOS,
      value: JSON.stringify(todos)
    })
    console.log('Todos cached successfully')
  } catch (error) {
    console.error('Error caching todos:', error)
  }
}

/**
 * キャッシュからToDoを取得
 */
export const getCachedTodos = async (): Promise<Todo[] | null> => {
  try {
    const { value } = await Preferences.get({ key: CACHE_KEYS.TODOS })
    if (value) {
      return JSON.parse(value)
    }
    return null
  } catch (error) {
    console.error('Error getting cached todos:', error)
    return null
  }
}

/**
 * 保留中の変更を追加
 */
export const addPendingChange = async (change: PendingChange): Promise<void> => {
  try {
    const { value } = await Preferences.get({ key: CACHE_KEYS.PENDING_CHANGES })
    const pendingChanges: PendingChange[] = value ? JSON.parse(value) : []

    pendingChanges.push(change)

    await Preferences.set({
      key: CACHE_KEYS.PENDING_CHANGES,
      value: JSON.stringify(pendingChanges)
    })
    console.log('Pending change added:', change)
  } catch (error) {
    console.error('Error adding pending change:', error)
  }
}

/**
 * 保留中の変更を取得
 */
export const getPendingChanges = async (): Promise<PendingChange[]> => {
  try {
    const { value } = await Preferences.get({ key: CACHE_KEYS.PENDING_CHANGES })
    if (value) {
      return JSON.parse(value)
    }
    return []
  } catch (error) {
    console.error('Error getting pending changes:', error)
    return []
  }
}

/**
 * 保留中の変更をクリア
 */
export const clearPendingChanges = async (): Promise<void> => {
  try {
    await Preferences.remove({ key: CACHE_KEYS.PENDING_CHANGES })
    console.log('Pending changes cleared')
  } catch (error) {
    console.error('Error clearing pending changes:', error)
  }
}

/**
 * 最終同期時刻を保存
 */
export const setLastSyncTime = async (timestamp: number): Promise<void> => {
  try {
    await Preferences.set({
      key: CACHE_KEYS.LAST_SYNC,
      value: timestamp.toString()
    })
  } catch (error) {
    console.error('Error setting last sync time:', error)
  }
}

/**
 * 最終同期時刻を取得
 */
export const getLastSyncTime = async (): Promise<number | null> => {
  try {
    const { value } = await Preferences.get({ key: CACHE_KEYS.LAST_SYNC })
    if (value) {
      return parseInt(value, 10)
    }
    return null
  } catch (error) {
    console.error('Error getting last sync time:', error)
    return null
  }
}

/**
 * すべてのキャッシュをクリア
 */
export const clearAllCache = async (): Promise<void> => {
  try {
    await Preferences.remove({ key: CACHE_KEYS.PROJECTS })
    await Preferences.remove({ key: CACHE_KEYS.TODOS })
    await Preferences.remove({ key: CACHE_KEYS.PENDING_CHANGES })
    await Preferences.remove({ key: CACHE_KEYS.LAST_SYNC })
    await Preferences.remove({ key: CACHE_KEYS.STATIC_CONTENT })
    console.log('All cache cleared')
  } catch (error) {
    console.error('Error clearing cache:', error)
  }
}

// ==========================================
// 静的コンテンツ（利用規約、プライバシーポリシー等）のキャッシュ
// ==========================================

export interface CachedStaticContent {
  title: string
  content: string
  version: number
  publishedAt: string
  updatedAt: string
  cachedAt: number // キャッシュ保存時刻
}

interface StaticContentCache {
  [key: string]: CachedStaticContent // key = `${contentType}_${language}`
}

/**
 * 静的コンテンツのキャッシュキーを生成
 */
const getStaticContentCacheKey = (contentType: string, language: string): string => {
  return `${contentType}_${language}`
}

/**
 * 静的コンテンツをキャッシュに保存
 */
export const cacheStaticContent = async (
  contentType: string,
  language: string,
  data: Omit<CachedStaticContent, 'cachedAt'>
): Promise<void> => {
  try {
    const { value } = await Preferences.get({ key: CACHE_KEYS.STATIC_CONTENT })
    const cache: StaticContentCache = value ? JSON.parse(value) : {}

    const key = getStaticContentCacheKey(contentType, language)
    cache[key] = {
      ...data,
      cachedAt: Date.now()
    }

    await Preferences.set({
      key: CACHE_KEYS.STATIC_CONTENT,
      value: JSON.stringify(cache)
    })
    console.log(`Static content cached: ${contentType} (${language})`)
  } catch (error) {
    console.error('Error caching static content:', error)
  }
}

/**
 * キャッシュから静的コンテンツを取得
 * キャッシュが期限切れの場合はnullを返す
 */
export const getCachedStaticContent = async (
  contentType: string,
  language: string,
  ignoreExpiry: boolean = false
): Promise<CachedStaticContent | null> => {
  try {
    const { value } = await Preferences.get({ key: CACHE_KEYS.STATIC_CONTENT })
    if (!value) return null

    const cache: StaticContentCache = JSON.parse(value)
    const key = getStaticContentCacheKey(contentType, language)
    const cached = cache[key]

    if (!cached) return null

    // 有効期限チェック（ignoreExpiryがtrueの場合はスキップ）
    if (!ignoreExpiry) {
      const isExpired = Date.now() - cached.cachedAt > STATIC_CONTENT_CACHE_TTL
      if (isExpired) {
        console.log(`Static content cache expired: ${contentType} (${language})`)
        return null
      }
    }

    return cached
  } catch (error) {
    console.error('Error getting cached static content:', error)
    return null
  }
}

/**
 * 特定の静的コンテンツのキャッシュをクリア
 */
export const clearStaticContentCache = async (
  contentType?: string,
  language?: string
): Promise<void> => {
  try {
    if (!contentType) {
      // 全ての静的コンテンツキャッシュをクリア
      await Preferences.remove({ key: CACHE_KEYS.STATIC_CONTENT })
      console.log('All static content cache cleared')
      return
    }

    const { value } = await Preferences.get({ key: CACHE_KEYS.STATIC_CONTENT })
    if (!value) return

    const cache: StaticContentCache = JSON.parse(value)

    if (language) {
      // 特定のコンテンツタイプと言語のキャッシュをクリア
      const key = getStaticContentCacheKey(contentType, language)
      delete cache[key]
    } else {
      // 特定のコンテンツタイプの全言語キャッシュをクリア
      Object.keys(cache).forEach(key => {
        if (key.startsWith(`${contentType}_`)) {
          delete cache[key]
        }
      })
    }

    await Preferences.set({
      key: CACHE_KEYS.STATIC_CONTENT,
      value: JSON.stringify(cache)
    })
    console.log(`Static content cache cleared: ${contentType}${language ? ` (${language})` : ''}`)
  } catch (error) {
    console.error('Error clearing static content cache:', error)
  }
}
